import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaChurch, FaClock, FaMapMarkerAlt, FaPray, FaPrayingHands, FaUsers, FaMusic, FaBible, FaHeart, FaHandsHelping, FaChild, FaCross, FaDove } from 'react-icons/fa';
import { API_URL } from '../../config';
import { motion, AnimatePresence } from 'framer-motion';

const ServicesPage = () => {
  const [branches, setBranches] = useState([]);
  const [isAddingService, setIsAddingService] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const availableIcons = {
    FaPrayingHands: <FaPrayingHands />,
    FaPray: <FaPray />,
    FaUsers: <FaUsers />,
    FaMusic: <FaMusic />,
    FaBible: <FaBible />,
    FaHeart: <FaHeart />,
    FaHandsHelping: <FaHandsHelping />,
    FaChild: <FaChild />,
    FaCross: <FaCross />,
    FaDove: <FaDove />,
    FaChurch: <FaChurch />
  };

  const getIconComponent = (iconName) => {
    return availableIcons[iconName] || <FaPrayingHands />;
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API_URL}/home-content/latest`);
      if (!response.ok) throw new Error('Failed to fetch branches');
      const data = await response.json();
      setBranches(data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setError('Failed to load branches');
    }
  };

  const handleSaveService = async (serviceData) => {
    try {
      if (!serviceData.branchId) {
        setError('Please select a branch');
        return;
      }

      console.log('Sending service data:', {
        branchId: serviceData.branchId,
        name: serviceData.name,
        time: serviceData.time,
        description: serviceData.description,
        icon: serviceData.icon
      });

      // Determine if we're editing or creating
      const isEditing = editingService && editingService._id;
      const url = isEditing 
        ? `${API_URL}/home-content/branches/${serviceData.branchId}/services/${editingService._id}`
        : `${API_URL}/home-content/services`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          branchId: serviceData.branchId,
          name: serviceData.name,
          time: serviceData.time,
          description: serviceData.description,
          icon: serviceData.icon
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to ${isEditing ? 'update' : 'add'} service`);
      }

      await response.json();
      setSuccess(`Service ${isEditing ? 'updated' : 'added'} successfully!`);
      setIsAddingService(false);
      setEditingService(null);
      
      // Immediately fetch updated branches data
      await fetchBranches();
      
      // Clear any existing error
      setError('');
    } catch (error) {
      console.error('Error saving service:', error);
      setError(error.message || 'Failed to save service. Please try again.');
    }
  };

  const handleDeleteService = async (branchId, serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const response = await fetch(
        `${API_URL}/home-content/branches/${branchId}/services/${serviceId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete service');
      }
      
      setSuccess('Service deleted successfully!');
      await fetchBranches();
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Failed to delete service');
    }
  };

  return (
    <PageContainer>
      <Header>
        <HeaderContent>
          <Title>Service Management</Title>
          <Subtitle>Add and manage services for each branch</Subtitle>
        </HeaderContent>
        <AddButtonContainer>
          <AddButton onClick={() => setIsAddingService(true)}>
            <FaPlus /> Add Service
          </AddButton>
        </AddButtonContainer>
      </Header>

      {error && <Alert type="error">{error}</Alert>}
      {success && <Alert type="success">{success}</Alert>}

      <AnimatePresence mode="wait">
        {isAddingService || editingService ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ServiceForm
              service={editingService}
              branches={branches}
              onSave={handleSaveService}
              onCancel={() => {
                setIsAddingService(false);
                setEditingService(null);
              }}
            />
          </motion.div>
        ) : (
          <BranchesContainer>
            {branches.map(branch => (
              <BranchSection key={branch._id}>
                <BranchHeader>
                  <BranchInfo>
                    <FaMapMarkerAlt />
                    <div>
                      <h2>{branch.name}</h2>
                      <p>{branch.location}</p>
                    </div>
                  </BranchInfo>
                </BranchHeader>
                
                {branch.services && branch.services.length > 0 ? (
                  <ServicesGrid>
                    {branch.services.map(service => (
                      <ServiceCard
                        key={service._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        whileHover={{ y: -5 }}
                      >
                        <ServiceIcon>
                          {getIconComponent(service.icon)}
                        </ServiceIcon>
                        <ServiceInfo>
                          <ServiceName>{service.name}</ServiceName>
                          <ServiceTimeWrapper>
                            <FaClock />
                            <ServiceTime>{service.time}</ServiceTime>
                          </ServiceTimeWrapper>
                          <ServiceDescription>{service.description}</ServiceDescription>
                        </ServiceInfo>
                        <ServiceActions>
                          <IconButton 
                            onClick={() => setEditingService({ ...service, branchId: branch._id })}
                            title="Edit service"
                          >
                            <FaEdit />
                          </IconButton>
                          <IconButton 
                            color="#dc3545"
                            onClick={() => handleDeleteService(branch._id, service._id)}
                            title="Delete service"
                          >
                            <FaTrash />
                          </IconButton>
                        </ServiceActions>
                      </ServiceCard>
                    ))}
                  </ServicesGrid>
                ) : (
                  <EmptyState>No services for this branch yet.</EmptyState>
                )}
              </BranchSection>
            ))}
          </BranchesContainer>
        )}
      </AnimatePresence>
    </PageContainer>
  );
};

const ServiceForm = ({ service, branches, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    branchId: '',
    name: '',
    time: '',
    description: '',
    icon: 'FaPrayingHands'
  });

  const [previewIcon, setPreviewIcon] = useState('FaPrayingHands');

  // Update form data when service prop changes (for editing)
  useEffect(() => {
    if (service) {
      setFormData({
        branchId: service.branchId || '',
        name: service.name || '',
        time: service.time || '',
        description: service.description || '',
        icon: service.icon || 'FaPrayingHands'
      });
      setPreviewIcon(service.icon || 'FaPrayingHands');
    } else {
      // Reset form for new service
      setFormData({
        branchId: '',
        name: '',
        time: '',
        description: '',
        icon: 'FaPrayingHands'
      });
      setPreviewIcon('FaPrayingHands');
    }
  }, [service]);

  const availableIcons = [
    { name: 'FaPrayingHands', component: <FaPrayingHands />, label: 'Praying Hands' },
    { name: 'FaPray', component: <FaPray />, label: 'Prayer' },
    { name: 'FaUsers', component: <FaUsers />, label: 'Group' },
    { name: 'FaMusic', component: <FaMusic />, label: 'Music' },
    { name: 'FaBible', component: <FaBible />, label: 'Bible' },
    { name: 'FaHeart', component: <FaHeart />, label: 'Heart' },
    { name: 'FaHandsHelping', component: <FaHandsHelping />, label: 'Helping Hands' },
    { name: 'FaChild', component: <FaChild />, label: 'Children' },
    { name: 'FaCross', component: <FaCross />, label: 'Cross' },
    { name: 'FaDove', component: <FaDove />, label: 'Dove' },
    { name: 'FaChurch', component: <FaChurch />, label: 'Church' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'icon') {
      setPreviewIcon(value);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.branchId) {
      setError('Please select a branch');
      return;
    }
    onSave(formData);
  };

  const getIconComponent = (iconName) => {
    const icon = availableIcons.find(i => i.name === iconName);
    return icon ? icon.component : <FaPrayingHands />;
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FormHeader>
        <h2>{service ? 'Edit Service' : 'Add New Service'}</h2>
        <p>Fill in the service details below</p>
      </FormHeader>

      <FormGroup>
        <Label>Branch</Label>
        <Select
          name="branchId"
          value={formData.branchId}
          onChange={handleChange}
          required
        >
          <option value="">Select a branch</option>
          {branches.map(branch => (
            <option key={branch._id} value={branch._id}>
              {branch.name} - {branch.location}
            </option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Service Icon</Label>
        <IconSelectionGrid>
          {availableIcons.map(icon => (
            <IconOption
              key={icon.name}
              type="button"
              $isSelected={formData.icon === icon.name}
              onClick={() => handleChange({ target: { name: 'icon', value: icon.name } })}
              title={icon.label}
            >
              {icon.component}
            </IconOption>
          ))}
        </IconSelectionGrid>
      </FormGroup>

      <FormGroup>
        <Label>Preview</Label>
        <ServicePreview>
          <PreviewCard>
            <ServiceIconPreview>
              {getIconComponent(previewIcon)}
            </ServiceIconPreview>
            <PreviewContent>
              <PreviewTitle>{formData.name || 'Service Name'}</PreviewTitle>
              <PreviewTime>
                <FaClock />
                {formData.time || 'Service Time'}
              </PreviewTime>
              <PreviewDescription>
                {formData.description || 'Service Description'}
              </PreviewDescription>
            </PreviewContent>
          </PreviewCard>
        </ServicePreview>
      </FormGroup>

      <FormGroup>
        <Label>Service Name</Label>
        <Input
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="e.g. Sunday Service"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>Service Time</Label>
        <Input
          name="time"
          value={formData.time}
          onChange={handleChange}
          placeholder="e.g. Sunday 8:30 AM"
          required
        />
      </FormGroup>

      <FormGroup>
        <Label>Description</Label>
        <TextArea
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Describe the service... (optional)"
          rows={4}
        />
      </FormGroup>

      <ButtonGroup>
        <Button type="submit" variant="primary">
          {service ? 'Update Service' : 'Add Service'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </ButtonGroup>
    </Form>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;

  ${props => props.variant === 'primary' && `
    background: ${props.theme.colors.primary};
    color: white;
    &:hover {
      background: ${props.theme.colors.primary}dd;
      transform: translateY(-2px);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: #6c757d;
    color: white;
    &:hover {
      background: #5a6268;
      transform: translateY(-2px);
    }
  `}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: white;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
`;

const AddButtonContainer = styled.div`
  margin-left: 2rem;
`;

const AddButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.9rem;
  border-radius: 8px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  svg {
    font-size: 0.8rem;
  }

  &:hover {
    background: ${props => props.theme.colors.primary}ee;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const BranchesContainer = styled.div`
  display: grid;
  gap: 2rem;
`;

const BranchSection = styled.section`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const BranchHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid ${props => props.theme.colors.primary}22;
`;

const BranchInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.secondary};
  }

  h2 {
    font-size: 1.4rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 0.25rem;
  }

  p {
    color: #666;
    font-size: 0.9rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.colors.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h3`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const ServiceTimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: ${props => props.theme.colors.secondary};

  svg {
    font-size: 0.9rem;
  }
`;

const ServiceTime = styled.div`
  font-size: 0.9rem;
`;

const ServiceDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
`;

const ServiceActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || '#666'};
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  font-size: 1.1rem;

  &:hover {
    background: ${props => props.color ? `${props.color}15` : '#f0f0f0'};
    color: ${props => props.color || props.theme.colors.primary};
    transform: scale(1.1);
  }
`;

const Form = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  margin: 0 auto;
`;

const FormHeader = styled.div`
  margin-bottom: 2rem;
  text-align: center;

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #4b5563;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}22;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Alert = styled.div`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  background: ${props => props.type === 'error' ? '#dc3545' : '#28a745'};
  color: white;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: #f8f9fa;
  border-radius: 8px;
  color: #666;
  font-size: 1.1rem;
`;

const IconSelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const IconOption = styled.button`
  width: 50px;
  height: 50px;
  border: 2px solid ${props => props.$isSelected ? props.theme.colors.primary : '#eee'};
  border-radius: 10px;
  background: ${props => props.$isSelected ? props.theme.colors.primary + '11' : 'white'};
  color: ${props => props.$isSelected ? props.theme.colors.primary : '#666'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  padding: 0;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ServicePreview = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
`;

const PreviewCard = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ServiceIconPreview = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.theme.colors.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
`;

const PreviewContent = styled.div`
  flex: 1;
`;

const PreviewTitle = styled.h3`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const PreviewTime = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 0.75rem;
  font-size: 0.9rem;

  svg {
    font-size: 0.9rem;
  }
`;

const PreviewDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
`;

export default ServicesPage; 